import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@welldev-group/rcl-button';
import Icon from '@welldev-group/rcl-icon';
import Input from '@welldev-group/rcl-input';
import DatePicker from '@welldev-group/rcl-date-picker';
import moment from 'moment';
import { I18nText } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import ClassNames from 'classnames';
import routes from '../../../../../shared/routes';
import httpClient from '../../../../../shared/libraries/httpClient';
import TextEditor from '../../../TextEditor';

const AffiliateLogCreateModal = ({ affiliateId, fetchAffiliateLogs }) => {
  const [body, setBody] = useState(null);
  const [incidentAt, setIncidentAt] = useState(null);
  const [summary, setSummary] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [errors, setErrors] = useState({});

  const onModalClose = () => {
    setModalOpen(false);
  };

  const onModalOpen = () => {
    setModalOpen(true);
  };

  const createAffiliateLog = async () => {
    const params = {
      affiliate_log: {
        incident_at: incidentAt,
        summary,
        body,
      },
    };
    const { data } = await httpClient.post(
      routes.affiliates.logs.create({ affiliate_id: affiliateId }),
      params,
    );
    if (data.error === null) {
      fetchAffiliateLogs().then(setModalOpen(false));
    }
  };

  const handleCreateAffiliateLog = () => {
    createAffiliateLog().catch(({ response }) => {
      if (response.status === 422) {
        setErrors(response.data.error);
      }
    });
  };

  const onDateChangeHandler = date => {
    if (date) setIncidentAt(moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD'));
  };

  return (
    <div className="affiliate-log__modal-container">
      <Button
        className="button button--v2 button--small button--primary"
        version="v2"
        size="small"
        type="primary"
        onClick={onModalOpen}
        label={
          <I18nText id="affiliate_three_sixty.affiliate.button_label.new" />
        }
      />

      <Modal
        className="modal__wrapper"
        isVisible={modalOpen}
        onOutsideClick={onModalClose}
      >
        <div className="modal__header">
          <Modal.Title>
            <I18nText id="affiliate_three_sixty.affiliate.log.create.header" />
          </Modal.Title>
          <div
            onClick={onModalClose}
            onKeyDown={onModalClose}
            role="presentation"
          >
            <Icon name="close" size="normal" />
          </div>
        </div>

        <div className="grid affiliate-log__modal-grid">
          <div className="col-12 modal__wrapper-overflow col-bleed">
            <Modal.Content>
              <div className="col-xlg-4 col-lg-5 col-12 affiliate-log__modal-fields--date col-bleed-y">
                <DatePicker
                  id={affiliateId}
                  focused={focused}
                  onFocusChange={setFocused}
                  firstDayOfWeek={0}
                  enablePastDates={false}
                  hideMonthsAndYearsWithNoActiveDates={true}
                  minDate={new Date().toISOString().split('T')[0]}
                  displayFormat="L"
                  fullWidth={true}
                  futureYearsCount={10}
                  hideNavButtons={false}
                  onChange={date => onDateChangeHandler(date)}
                  date={incidentAt}
                  error={errors.incidentAt}
                  required={true}
                />
              </div>

              <div className="col-xlg-8 col-lg-7 col-12 affiliate-log__modal-fields--input col-bleed-y">
                <Input
                  onChange={value => setSummary(value)}
                  placeholder={I18n.t(
                    'components.affiliate_three_sixty.affiliate.log.create.placeholder.summary',
                  )}
                  value={summary}
                  isClearable={true}
                  touched={!!errors.summary}
                  error={errors.summary}
                  required={true}
                />
              </div>

              <div
                className={ClassNames(
                  'col-12 affiliate-log__modal-fields--textarea',
                  {
                    'affiliate-log__modal-fields--error': errors.body,
                  },
                )}
              >
                <TextEditor
                  onChange={setBody}
                  value={body}
                  error={errors.body}
                />
              </div>
            </Modal.Content>
          </div>

          <Modal.Footer>
            <div>
              <Button
                onClick={handleCreateAffiliateLog}
                label={
                  <I18nText id="affiliate_three_sixty.affiliate.button_label.save" />
                }
                version="v2"
                size="small"
                type="primary"
              />

              <Button
                onClick={onModalClose}
                label={
                  <I18nText id="affiliate_three_sixty.affiliate.button_label.cancel" />
                }
                version="v2"
                size="small"
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

AffiliateLogCreateModal.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  fetchAffiliateLogs: PropTypes.func.isRequired,
};

export default AffiliateLogCreateModal;
